import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { ToolTipUI } from '../../../widgets/components/UI/ToolTipUI'
import { AnalyticsBarColumnChart } from '../Overview/AnalyticsBarColumnChart'
import { DepartmentWiseChartProps } from '../../core/_models'
import { getRecordKeeperUserListApi, getStructureClassificationLevel1Api } from '../../core/_requests'
import { useAuth } from '../../../auth'
import { Dropdown, FormCheck, FormControl } from 'react-bootstrap'
import Skeleton from 'react-loading-skeleton'
import { toAbsoluteUrl } from '../../../../../_metronic/helpers'

const DepartmentWiseActivity = ({
  chartData,
  heading,
  tooltip,
  selectedOptions,
  setSelectedOptions,
  graphType,
}: {
  chartData: DepartmentWiseChartProps
  heading: string
  tooltip: string
  selectedOptions: any[]
  setSelectedOptions: Dispatch<SetStateAction<any[]>>
  graphType: 'department' | 'user'
}) => {
  const { formatMessage } = useIntl()
  const { companyId } = useAuth()
  const [isLevel1Loading, setIsLevel1Loading] = useState(false)
  const [level1Options, setLevel1Options] = useState<any[]>([])

  const [searchTerm, setSearchTerm] = useState('')

  const getStructureClassificationLevel1 = async () => {
    if (!companyId) return
    setIsLevel1Loading(true)
    try {
      const {
        data: { success, data },
      } = await getStructureClassificationLevel1Api(companyId)
      if (success) {
        if (graphType === 'department') setLevel1Options(data)
      }
    } catch (error) {
      console.error(error)
    } finally {
      setIsLevel1Loading(false)
    }
  }

  useEffect(() => {
    getStructureClassificationLevel1()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const getRecordKeeperUserList = async () => {
    if (!companyId) return
    setIsLevel1Loading(true)
    try {
      const {
        data: { success, data },
      } = await getRecordKeeperUserListApi(companyId)
      if (success) {
        if (graphType === 'user') setLevel1Options(data)
      }
    } catch (error) {
      console.error(error)
    } finally {
      setIsLevel1Loading(false)
    }
  }

  useEffect(() => {
    if (graphType === 'department') {
      getStructureClassificationLevel1()
    }

    if (graphType === 'user') {
      getRecordKeeperUserList()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleCheckboxChange = (option: any) => {
    if (selectedOptions.some((item) => item.id === option.id)) {
      setSelectedOptions(selectedOptions.filter((item) => item.id !== option.id))
    } else {
      setSelectedOptions([...selectedOptions, option])
    }
  }

  const filteredOptions = searchTerm
    ? level1Options.filter((option) =>
      option?.label?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    : level1Options

  return (
    <>
      <div className='bg-white rounded-2 p-4 department-wise'>
        <div className='px-4 pb-0 d-flex flex-md-row flex-column justify-content-between'>
          <div className='d-flex align-items-center gap-1 w-100'>
            <h4 className='m-0 font-size-14 text-muted fw-normal mb-2 mb-md-0'>
              {formatMessage({ id: heading })}
            </h4>
            <ToolTipUI tooltipText={formatMessage({ id: tooltip })} />
          </div>
          <div className='w-100 d-flex mb-2 mb-md-0 gap-3'>
            <div className='d-flex align-items-center gap-3'>
              <div
                style={{
                  width: '14px',
                  height: '14px',
                  borderRadius: '50%',
                  backgroundColor: '#4776E6',
                }}
              />
              <div className='d-flex me-3'>{formatMessage({ id: 'Edited' })}</div>
            </div>
            <div className='d-flex align-items-center gap-3'>
              <div
                style={{
                  width: '14px',
                  height: '14px',
                  borderRadius: '50%',
                  backgroundColor: '#0D216D',
                }}
              />
              <div className='d-flex me-3'>{formatMessage({ id: 'Shared' })}</div>
            </div>
            <div className='d-flex align-items-center gap-3'>
              <div
                style={{
                  width: '14px',
                  height: '14px',
                  borderRadius: '50%',
                  backgroundColor: '#1442B0',
                }}
              />
              <div className='d-flex me-3'>{formatMessage({ id: 'Renamed' })}</div>
            </div>
          </div>
          {isLevel1Loading ? (
            <Skeleton
              width={100}
              height={30}
              baseColor='var(--kt-skeleton-bg-color)'
              highlightColor='var(--kt-skeleton-highlight-bg-color)'
            />
          ) : (
            <div className='dropdown-center bg-white border rounded'>
              <Dropdown>
                <Dropdown.Toggle variant='outline-secondary' id='dropdown-basic' className='btn-sm'>
                  {selectedOptions.length === 0
                    ? `All ${graphType === 'department' ? 'Departments' : 'Users'}`
                    : `Selected ${selectedOptions.length} of ${level1Options.length}`}
                </Dropdown.Toggle>

                <Dropdown.Menu
                  style={{
                    maxHeight: '300px',
                    overflowY: 'auto',
                  }}
                  className='px-3'
                >
                  {/* All Departments Label */}

                  {/* Search Input */}
                  <div className='px-3 py-2'>
                    <FormControl
                      type='text'
                      placeholder={`Search ${graphType === 'department' ? 'Departments' : 'Users'}`}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>

                  <Dropdown.Item
                    as='div'
                    className='d-flex align-items-center px-3'
                    onClick={(e) => {
                      e.stopPropagation()
                      setSelectedOptions([])
                    }}
                  >
                    <div
                      className={`d-flex justify-content-between w-100 my-3 ${selectedOptions.length === 0 ? 'selected-color p-3' : ''
                        }`}
                    >
                      <div> {`All ${graphType === 'department' ? 'Departments' : 'Users'}`}</div>
                      {selectedOptions.length === 0 && (
                        <img
                          src={toAbsoluteUrl('/media/icons/RecordsKeeperAI/ic-tickmark.svg')}
                          alt='tick icon'
                        />
                      )}
                    </div>
                  </Dropdown.Item>

                  <div className='mx-3'>
                    <div>
                      Selected {selectedOptions.length} of {level1Options.length}
                    </div>
                    <hr className='mt-2' />
                  </div>

                  {/* Department List */}
                  {filteredOptions.length > 0 ? (
                    filteredOptions.map((option) => (
                      <Dropdown.Item
                        key={option.id}
                        as='div'
                        className='d-flex align-items-center px-3'
                        onClick={(e) => e.stopPropagation()} // Prevent dropdown from closing
                      >
                        <FormCheck
                          type='checkbox'
                          checked={selectedOptions.some((item) => item.id === option.id)}
                          onChange={() => handleCheckboxChange(option)}
                          label={
                            graphType === 'department' ? (
                              option.label.split(': ')[1]
                            ) : (
                              <div className='d-flex flex-column'>
                                <div>{option.label.split(',')[0]}</div>
                                <div className='text-muted small'>{option.label.split(',')[1]}</div>
                              </div>
                            )
                          }
                          className='text-dark'
                        />
                      </Dropdown.Item>
                    ))
                  ) : (
                    <div className='px-3 py-2'>
                      No {graphType === 'department' ? 'departments' : 'users'} found
                    </div>
                  )}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          )}
        </div>

        {chartData && <AnalyticsBarColumnChart dataActivity={chartData} />}
      </div>
    </>
  )
}

export default DepartmentWiseActivity
